import { resolvePath, type DocumentV3 } from '@docgen/schema';
import { setDocData } from './doc-ops';
import { toDateInput } from './money';

/**
 * The deal facts a rep changes from a phone, and where each lives in the
 * proposal's `document.data`. These are the same paths the template binds and
 * the server derives the introduction from (`apps/api/src/lib/proposals/
 * derived-content.ts`, `seed.ts`), so a quick edit is an ordinary document
 * edit: it autosaves through the same PUT, and the server rebuilds the intro.
 */
export const QUICK_EDIT_PATH = {
  introNote: 'intro.note',
  eventDate: 'event.date',
  eventCity: 'event.city',
  contactFirstName: 'contact.firstName',
} as const;

export type QuickEditField = keyof typeof QUICK_EDIT_PATH;

/** The quick-edit form's values, as plain strings. */
export type QuickEditValues = Readonly<Record<QuickEditField, string>>;

/**
 * Noon UTC: a calendar date stored as an instant must not slide to the day
 * before or after in any timezone that reads it.
 */
const CALENDAR_DATE_TIME = 'T12:00:00.000Z';

function stringAt(doc: DocumentV3, path: string): string {
  const value = resolvePath(doc.data, path);
  return typeof value === 'string' ? value : '';
}

const HTML_TAG = /<[a-z][\s\S]*>/i;

const ENTITIES: Readonly<Record<string, string>> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};

/**
 * The rep's introduction note as plain text for a textarea. A note set by an
 * inline edit on the desktop canvas is HTML; paragraphs become blank lines and
 * line breaks become newlines, which the server turns back into paragraphs.
 */
export function noteForEditing(note: string): string {
  if (!HTML_TAG.test(note)) return note;
  return note
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (entity) => ENTITIES[entity] ?? entity)
    .trim();
}

/** Reads the quick-edit values out of a proposal document. */
export function readQuickEdits(doc: DocumentV3): QuickEditValues {
  return {
    introNote: noteForEditing(stringAt(doc, QUICK_EDIT_PATH.introNote)),
    eventDate: toDateInput(stringAt(doc, QUICK_EDIT_PATH.eventDate)),
    eventCity: stringAt(doc, QUICK_EDIT_PATH.eventCity),
    contactFirstName: stringAt(doc, QUICK_EDIT_PATH.contactFirstName),
  };
}

/**
 * Writes one quick edit into the document. The event date arrives as the
 * DatePicker's `yyyy-mm-dd` (or '' to clear) and is stored as a timestamp.
 */
export function applyQuickEdit(doc: DocumentV3, field: QuickEditField, value: string): DocumentV3 {
  const stored = field === 'eventDate' && value !== '' ? `${value}${CALENDAR_DATE_TIME}` : value;
  return setDocData(doc, QUICK_EDIT_PATH[field], stored);
}
